import { useNavigate } from 'react-router-dom'
import Footer from '../../components/Layout/Footer'
import Logo from '../../graphics/Logo'
import { setPageTitle } from '../../utils/utilities'
import styles from './AudioVideoTroubleshooting.module.css'

const AudioVideoTroubleshooting = () => {
  setPageTitle('Audio & Video Troubleshooting')

  const navigate = useNavigate()
  const handleGoBack = () => {
    navigate('/', { replace: true })
  }

  return (
    <main className="flex flex-col h-screen overflow-y-auto xl:p-5 bg-primary-gradient">
      <div className="flex flex-col gap-5 lg:w-2/3 xl:w-1/2 justify-center mb-auto md:m-auto">
        <div className="container sm:rounded-lg bg-neutral-100 dark:bg-neutral-900 w-fit mx-auto p-5 shadow-lg">
          <div className="flex flex-col justify-between items-center gap-5">
            <div className="h-16 w-fit mx-auto my-5">
              <Logo />
            </div>
            <button className="button-outline" onClick={handleGoBack}>
              ← Go Back
            </button>
            <h1 className="text-center font-bold tracking-wide">
              Audio / Video Troubleshooting
            </h1>
            <div className={styles.wrapper}>
              <AudioHelpGuideText />
            </div>
            <button className="button-outline" onClick={handleGoBack}>
              ← Go Back
            </button>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <Footer />
      </div>
    </main>
  )
}

const AudioHelpGuideText = () => {
  return (
    <div className="flex flex-col gap-2">
      <h1>
        <strong>For Google Chrome:</strong>
      </h1>
      <h2>
        <strong>Play Audio:</strong>
      </h2>
      <p>
        Click on the play button for the audio or video content you wish to
        listen to.
      </p>
      <h2>
        <br />
      </h2>
      <h2>
        <strong>Unmute the Tab (if needed):</strong>
      </h2>
      <p>
        If you see a small speaker icon in the tab that&apos;s crossed out, it
        means the tab is muted. Right-click on the tab and select &quot;Unmute
        site&quot; from the context menu. This will allow audio to play from
        that tab.
      </p>
      <p>
        <br />
      </p>
      <h2>
        <strong>Adjust Volume:</strong>
      </h2>
      <p>
        Use the volume controls on your device to adjust the sound to your
        desired level.
      </p>
      <h2>
        <br />
      </h2>
      <h2>
        <strong>Allow Autoplay (if applicable):</strong>
      </h2>
      <p>If you want audio to automatically play when you open a site:</p>
      <ul>
        <li>Click on the lock icon in the address bar (left side).</li>
        <li>Select &quot;Site settings&quot;.</li>
        <li>
          Scroll down to the &quot;Sound&quot; option and select
          &quot;Allow&quot; to let the site play sound automatically.
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Adjusting Sound Settings in Chrome:</strong>
      </h2>
      <ul>
        <li>Open Google Chrome.</li>
        <li>
          Click on the three dots in the top-right corner for &apos;Customize
          and control Google Chrome&apos;, then click on &apos;Settings&apos;.
        </li>
        <li>Scroll down and click on &apos;Advanced&apos;.</li>
        <li>
          Under &apos;Privacy and security&apos;, click on &apos;Site
          settings&apos;.
        </li>
        <li>Click on &apos;Sound&apos;.</li>
        <li>
          Here, you can either &apos;Allow sites to play sound&apos; or
          &apos;Mute sites that play sound&apos; as default settings. You can
          also manage exceptions for specific sites
        </li>
      </ul>
      <p>
        <br />
      </p>
      <p>
        <em>
          Remember, the steps can slightly vary depending on the version of
          Chrome and the operating system you&apos;re using. If you&apos;re
          still facing issues, checking the support page of Google Chrome
        </em>
      </p>
      <p>
        <br />
      </p>
      <h1>
        <strong>For Firefox:</strong>
      </h1>
      <p>
        <strong>Adjust Autoplay Settings:</strong>
      </p>
      <ul>
        <li>
          Click on the Menu bar at the top of the screen and select
          &apos;Preferences&apos; or click on the hamburger icon on the top
          right and select &apos;Settings&apos;.
        </li>
        <li>
          Go to the &apos;Privacy &amp; Security&apos; panel and scroll down to
          the &apos;Permissions&apos; section.
        </li>
        <li>
          Click on the &apos;Settings&apos; button next to &apos;Autoplay&apos;.
        </li>
        <li>
          Here, you can choose the default behavior for media autoplay for all
          websites using the &apos;Default for all websites&apos; drop-down
          menu. Options include blocking audio, blocking audio and video, or
          allowing both.
        </li>
        <li>After making your selection, click &apos;Save Changes&apos;</li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Controlling Autoplay for Specific Sites:</strong>
      </h2>
      <ul>
        <li>
          If Firefox is actively blocking media on a website, you&apos;ll see a
          special icon in the address bar.
        </li>
        <li>
          Click on this icon to open the Site Information drop-down panel, where
          you can change the Autoplay setting for that specific website
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Mute or Unmute Individual Tabs:</strong>
      </h2>
      <ul>
        <li>
          In Firefox, each tab that is playing audio will show a small speaker
          icon next to it.
        </li>
        <li>
          To mute a tab, right-click on it and select &apos;Mute Tab&apos;. You
          can also use the keyboard shortcut Ctrl + M.
        </li>
        <li>
          To unmute, right-click on the tab and select &apos;Unmute Tab&apos;
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Manage Autoplay Exceptions:</strong>
      </h2>
      <ul>
        <li>
          To allow certain websites to autoplay content with sound, keep the
          &apos;Block websites from automatically playing sound&apos; option
          enabled in Firefox Options and click on &apos;Exceptions&apos; next to
          it.
        </li>
        <li>
          Type the website domain or URL in the address field and click on the
          &apos;Allow&apos; button. This will add the website to the whitelist
          and allow it to autoplay video with sound
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h1>
        <strong>For Safari:</strong>
      </h1>
      <h2>
        <strong>Check if the Tab is Muted:</strong>
      </h2>
      <ul>
        <li>Play an audio or video in a Safari tab.</li>
        <li>
          While the media is playing, look for an audio (speaker) icon on the
          right side of the tab.
        </li>
        <li>Click on the tab to unmute the audio, if it&apos;s muted.</li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Adjust Safari Preferences for Website Autoplay:</strong>
      </h2>
      <ul>
        <li>
          Open the website where you&apos;re experiencing playback issues in
          Safari.
        </li>
        <li>
          Click on &apos;Safari&apos; in the top-left corner of the menu bar.
        </li>
        <li>
          Select &apos;Settings for [website name]&apos; from the dropdown menu.
        </li>
        <li>
          In the pop-up menu, check if &apos;Stop Media with Sound&apos; is
          enabled next to &apos;Auto-Play&apos;.
        </li>
        <li>
          If it is, tap on the option to reveal a dropdown menu and select
          &apos;Allow All Auto-Play&apos;.
        </li>
      </ul>
      <h2>
        <br />
      </h2>
      <h2>
        <strong>Alternatively, you can do this from Safari Preferences:</strong>
      </h2>
      <ul>
        <li>
          Click on &apos;Safari&apos; in the top menu bar and select
          &apos;Preferences&apos;.
        </li>
        <li>Click on the &apos;Websites&apos; tab.</li>
        <li>Select &apos;Auto-Play&apos; from the left menu.</li>
        <li>In the Auto-Play tab on the right, select the website name.</li>
        <li>
          Click the dropdown tab on the right side and select &apos;Allow All
          Auto-Play&apos;.
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h2>
        <strong>Mute or Unmute Audio in Tabs:</strong>
      </h2>
      <ul>
        <li>
          To mute audio in the tab you&apos;re viewing, click the Audio button
          in the Smart Search field.
        </li>
        <li>
          To mute audio in other tabs while allowing audio in the tab
          you&apos;re currently viewing, Option-click the Audio button in the
          Smart Search field.
        </li>
        <li>
          To mute all audio, click the Audio button in the Smart Search field if
          you&apos;re viewing a website that&apos;s not playing audio and other
          tabs are playing audio.
        </li>
        <li>
          To unmute, click the muted Audio button in the Smart Search field or
          on the right side of the tab in the tab bar.
        </li>
      </ul>
      <p>
        <br />
      </p>
      <h1>
        <strong>For Microsoft Edge:</strong>
      </h1>
      <h2>Adjust Sound Settings:</h2>
      <ul>
        <li>
          Click or tap on the &quot;Settings and more&quot; (three dots) menu
          icon, usually located at the top right corner of the browser. From the
          dropdown menu, choose &quot;Settings&quot;.
        </li>
        <li>
          Navigate to Site Permissions: In the Settings menu, select
          &quot;Cookies and site permissions&quot; from the list on the left
          side of the screen.
        </li>
        <li>
          Adjust Media Autoplay Settings: Within the &quot;Cookies and site
          permissions&quot; section, find and click on &quot;Media
          autoplay&quot;. Here, you&apos;ll have the option to control how media
          autoplay is handled on websites. You can select from options like
          &apos;Allow&apos;, &apos;Limit&apos;, or &apos;Block&apos;, depending
          on your preference.
        </li>
        <li>
          Save Your Changes: After adjusting your settings, just close the
          settings tab, and your preferences should be saved automatically.
        </li>
      </ul>
    </div>
  )
}

export default AudioVideoTroubleshooting
